import { useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import NewsListItem from "@/components/feature/NewsListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { allNews } from "@/utils/catalog";
import type { News } from "@/types";

const PAGE_SIZE = 6;

export default function NewsCategoryPage() {
  const [filters, setFilters] = useState<Record<string, string>>({ channel: "全部" });
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const channels = useMemo(
    () => [
      { label: "全部", value: "全部" },
      ...Array.from(new Set(allNews.map((n) => n.channel))).map((v) => ({ label: v, value: v })),
    ],
    []
  );

  const filtered = useMemo(() => {
    const list: News[] = allNews.filter(
      (n) => filters.channel === "全部" || n.channel === filters.channel
    );
    return [...list].sort((a, b) =>
      sort === "newest"
        ? new Date(b.date).getTime() - new Date(a.date).getTime()
        : new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ channel: "全部" });
    setSort("newest");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-6 pb-2">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯" }]} />
      </div>

      <div className="w-full px-4 md:px-8 pt-4 pb-10 md:pb-14">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-newspaper-line text-lg"></i>
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
              娱乐资讯
            </h1>
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
            影视资讯、明星资讯与栏目综述，聚合最新娱乐行业动态，按频道快速筛选感兴趣的内容。
          </p>
        </div>

        <FilterBar
          groups={[{ key: "channel", label: "频道", options: channels }]}
          values={filters}
          onChange={handleFilterChange}
          sorts={[
            { label: "最新发布", value: "newest" },
            { label: "最早发布", value: "oldest" },
          ]}
          sortValue={sort}
          onSortChange={(v) => {
            setSort(v);
            setPage(1);
          }}
        />

        <div className="mt-5 mb-4 text-sm text-foreground-500">
          共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条资讯
        </div>

        {paged.length > 0 ? (
          <>
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
              {paged.map((item) => (
                <NewsListItem key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-10">
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <EmptyState
            title="暂无符合条件的资讯内容"
            description="试试切换频道，或点击下方按钮重置全部筛选。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}